import { ImagePlus, Loader2, Plus, Trash2, Video, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { adminApi } from "../../api/client";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { useToast } from "../../context/ToastContext";
import type { AdminVenue, EventAdmissionType, EventCategory, EventItem } from "../../types/api";

type FormErrors = Record<string, string>;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateEventForm(form: EventFormState): FormErrors {
  const errors: FormErrors = {};

  if (!form.title.trim()) errors.title = "Title is required";
  if (!form.category.trim()) errors.category = "Category is required";
  if (!form.city.trim()) errors.city = "City is required";
  if (!form.venue.trim()) errors.venue = "Venue is required";
  if (!form.address.trim()) errors.address = "Address is required";
  if (!form.date) errors.date = "Date is required";
  if (!form.time) errors.time = "Start time is required";
  if (!form.image.trim()) errors.image = "A cover image is required";
  if (!form.description.trim()) errors.description = "Description is required";
  if (!form.organizerName.trim()) errors.organizerName = "Exhibitor name is required";

  if (!form.organizerEmail.trim()) {
    errors.organizerEmail = "Exhibitor email is required";
  } else if (!EMAIL_PATTERN.test(form.organizerEmail.trim())) {
    errors.organizerEmail = "Enter a valid email address";
  }

  const capacity = Number(form.capacity);
  if (!form.capacity || !Number.isFinite(capacity) || capacity < 1) {
    errors.capacity = "Capacity must be at least 1";
  }

  if (form.ticketTiers.length === 0) {
    errors.ticketTiers = "Add at least one ticket tier";
  }

  form.ticketTiers.forEach((tier, index) => {
    if (!tier.name.trim()) errors[`ticketTiers.${index}.name`] = "Tier name is required";

    const price = Number(tier.price);
    if (tier.price === "" || !Number.isFinite(price) || price < 0) {
      errors[`ticketTiers.${index}.price`] = "Price must be 0 or more";
    }

    const quantity = Number(tier.quantity);
    if (!tier.quantity || !Number.isFinite(quantity) || quantity < 1) {
      errors[`ticketTiers.${index}.quantity`] = "Quantity must be at least 1";
    }
  });

  return errors;
}

type TierDraft = {
  id?: string;
  name: string;
  description: string;
  price: string;
  quantity: string;
  sold: string;
  perks: string;
};

type EventFormState = {
  title: string;
  category: string;
  status: EventItem["status"];
  admissionType: EventAdmissionType;
  featured: boolean;
  city: string;
  venue: string;
  address: string;
  date: string;
  time: string;
  doorsOpen: string;
  image: string;
  landscapeImage: string;
  trailerVideo: string;
  gallery: string[];
  description: string;
  highlights: string;
  organizerName: string;
  organizerEmail: string;
  sponsor: string;
  capacity: string;
  ticketTiers: TierDraft[];
};

const defaultState: EventFormState = {
  title: "",
  category: "",
  status: "draft",
  admissionType: "ticket",
  featured: false,
  city: "",
  venue: "",
  address: "",
  date: "",
  time: "",
  doorsOpen: "",
  image: "",
  landscapeImage: "",
  trailerVideo: "",
  gallery: [],
  description: "",
  highlights: "",
  organizerName: "",
  organizerEmail: "",
  sponsor: "",
  capacity: "500",
  ticketTiers: [
    {
      name: "Standard",
      description: "Main access ticket",
      price: "80",
      quantity: "300",
      sold: "0",
      perks: "Main bowl access\nGeneral entry",
    },
  ],
};

function defaultEventFormState(
  organizer?: {
    name?: string | null;
    email?: string | null;
  } | null
): EventFormState {
  return {
    ...defaultState,
    gallery: [...defaultState.gallery],
    organizerName: organizer?.name ?? "",
    organizerEmail: organizer?.email ?? "",
    ticketTiers: defaultState.ticketTiers.map((tier) => ({ ...tier })),
  };
}

function toFormState(
  event?: EventItem | null,
  organizer?: {
    name?: string | null;
    email?: string | null;
  } | null
): EventFormState {
  if (!event) return defaultEventFormState(organizer);
  return {
    title: event.title,
    category: event.category,
    status: event.status,
    admissionType: event.admissionType ?? "ticket",
    featured: event.featured,
    city: event.city,
    venue: event.venue,
    address: event.address,
    date: event.date,
    time: event.time,
    doorsOpen: event.doorsOpen ?? "",
    image: event.image,
    landscapeImage: event.landscapeImage ?? "",
    trailerVideo: event.trailerVideo ?? "",
    gallery: [...event.gallery],
    description: event.description,
    highlights: event.highlights.join("\n"),
    organizerName: event.organizerName,
    organizerEmail: event.organizerEmail,
    sponsor: event.sponsor ?? "",
    capacity: String(event.capacity),
    ticketTiers: event.ticketTiers.map((tier) => ({
      id: tier.id,
      name: tier.name,
      description: tier.description,
      price: String(tier.price),
      quantity: String(tier.quantity),
      sold: String(tier.sold),
      perks: tier.perks.join("\n"),
    })),
  };
}

function splitMultiline(value: string) {
  return value
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
}

export default function EventEditor({
  event,
  isOpen,
  isSaving,
  onClose,
  onSubmit,
}: {
  event?: EventItem | null;
  isOpen: boolean;
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (payload: Partial<EventItem>) => Promise<void>;
}) {
  const { showToast } = useToast();
  const { admin, token } = useAdminAuth();
  const [form, setForm] = useState<EventFormState>(defaultState);
  const [errors, setErrors] = useState<FormErrors>({});
  const [catalogCategories, setCatalogCategories] = useState<EventCategory[]>([]);
  const [catalogVenues, setCatalogVenues] = useState<AdminVenue[]>([]);
  const isOrganizer = admin?.role === "organizer";

  useEffect(() => {
    setForm(toFormState(event, admin));
    setErrors({});
  }, [admin, event, isOpen]);

  useEffect(() => {
    if (!isOpen || !token) return;
    let active = true;

    adminApi
      .listCatalog(token)
      .then((response) => {
        if (!active) return;
        setCatalogCategories(response.categories);
        setCatalogVenues(response.venues);
      })
      .catch(() => {
        if (active) {
          setCatalogCategories([]);
          setCatalogVenues([]);
        }
      });

    return () => {
      active = false;
    };
  }, [isOpen, token]);

  const activeCategories = useMemo(
    () => catalogCategories.filter((category) => category.active),
    [catalogCategories]
  );
  const activeVenues = useMemo(() => catalogVenues.filter((venue) => venue.active), [catalogVenues]);
  const hasCurrentCategoryOption = Boolean(
    form.category && !activeCategories.some((category) => category.name === form.category)
  );
  const hasCurrentVenueOption = Boolean(
    form.venue && !activeVenues.some((venue) => venue.name === form.venue)
  );

  const updateField = (key: keyof EventFormState, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
    if (errors[key]) {
      setErrors((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
    }
  };

  const updateVenueFromCatalog = (value: string) => {
    const selectedVenue = activeVenues.find((venue) => venue.name === value);
    setForm((current) => ({
      ...current,
      venue: value,
      ...(selectedVenue
        ? {
            city: selectedVenue.city,
            address: selectedVenue.address,
            capacity: selectedVenue.capacity > 0 ? String(selectedVenue.capacity) : current.capacity,
          }
        : {}),
    }));

    if (errors.venue || (selectedVenue && (errors.city || errors.address || errors.capacity))) {
      setErrors((current) => {
        const next = { ...current };
        delete next.venue;
        if (selectedVenue) {
          delete next.city;
          delete next.address;
          delete next.capacity;
        }
        return next;
      });
    }
  };

  const updateTier = (index: number, key: keyof TierDraft, value: string) => {
    setForm((current) => ({
      ...current,
      ticketTiers: current.ticketTiers.map((entry, tierIndex) =>
        tierIndex === index ? { ...entry, [key]: value } : entry
      ),
    }));
    const errorKey = `ticketTiers.${index}.${key}`;
    if (errors[errorKey]) {
      setErrors((current) => {
        const next = { ...current };
        delete next[errorKey];
        return next;
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch bg-black/60 p-3 backdrop-blur-md dark:bg-black/75 sm:p-6">
      <div className="mx-auto flex h-full w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-surface shadow-2xl">
        <div className="flex items-start justify-between border-b border-line px-6 py-5">
          <div>
            <span className="ego-badge ego-badge-brand">{event ? "Edit screening" : "New screening"}</span>
            <h2 className="mt-2 text-xl font-bold tracking-tight text-foreground">
              {event ? event.title : "Create a new screening"}
            </h2>
            {isOrganizer && (
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                Choose Draft to keep this screening private, or Published to make it live for customers.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-xl text-muted-foreground transition hover:bg-surface-muted hover:text-foreground"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form
          id="event-editor-form"
          className="flex min-h-0 flex-1 flex-col overflow-y-auto lg:grid lg:grid-cols-[1fr_360px] lg:overflow-hidden"
          onSubmit={async (eventSubmit) => {
            eventSubmit.preventDefault();

            const validationErrors = validateEventForm(form);
            if (Object.keys(validationErrors).length > 0) {
              setErrors(validationErrors);
              showToast("Please fix the highlighted fields and try again", "error");
              return;
            }

            setErrors({});
            await onSubmit({
              title: form.title,
              category: form.category,
              status: form.status,
              admissionType: form.admissionType,
              featured: form.featured,
              city: form.city,
              venue: form.venue,
              address: form.address,
              date: form.date,
              time: form.time,
              doorsOpen: form.doorsOpen,
              image: form.image,
              landscapeImage: form.landscapeImage,
              trailerVideo: form.trailerVideo,
              gallery: form.gallery,
              description: form.description,
              highlights: splitMultiline(form.highlights),
              organizerName: form.organizerName,
              organizerEmail: form.organizerEmail,
              sponsor: form.sponsor,
              capacity: Number(form.capacity),
              ticketTiers: form.ticketTiers.map((tier) => ({
                id: tier.id,
                name: tier.name,
                description: tier.description,
                price: Number(tier.price),
                quantity: Number(tier.quantity),
                sold: Number(tier.sold),
                perks: splitMultiline(tier.perks),
              })),
            });
          }}
        >
          <div className="space-y-6 px-6 py-6 lg:min-h-0 lg:overflow-y-auto">
            <section>
              <h3 className="ego-eyebrow">
                Cover image <RequiredMark />
              </h3>
              <ImageUploadField
                value={form.image}
                error={errors.image}
                onChange={(url) => updateField("image", url)}
              />
            </section>

            <section>
              <h3 className="ego-eyebrow">Landscape event image</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Used for the full-width homepage event slider. Upload a landscape image only.
              </p>
              <ImageUploadField
                value={form.landscapeImage}
                onChange={(url) => updateField("landscapeImage", url)}
                previewAlt="Landscape event artwork"
                requireLandscape
              />
            </section>

            <section>
              <h3 className="ego-eyebrow">Trailer video (optional)</h3>
              <VideoUploadField
                value={form.trailerVideo}
                error={errors.trailerVideo}
                onChange={(url) => updateField("trailerVideo", url)}
              />
            </section>

            <section>
              <h3 className="ego-eyebrow">Basics</h3>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                {(
                  [
                    { label: "Title", key: "title", required: true },
                    { label: "Category", key: "category", required: true },
                    { label: "City", key: "city", required: true },
                    { label: "Venue", key: "venue", required: true },
                    { label: "Date", key: "date", type: "date", required: true },
                    { label: "Start time", key: "time", type: "time", required: true },
                    { label: "Doors open", key: "doorsOpen", type: "time" },
                    { label: "Capacity", key: "capacity", type: "number", required: true },
                    { label: "Exhibitor name", key: "organizerName", required: true },
                    { label: "Exhibitor email", key: "organizerEmail", type: "email", required: true },
                    { label: "Sponsor", key: "sponsor" },
                  ] satisfies ReadonlyArray<{
                    label: string;
                    key: keyof EventFormState;
                    type?: string;
                    required?: boolean;
                  }>
                ).map((field) => {
                  const errorMessage = errors[field.key];
                  if (field.key === "category") {
                    return (
                      <label key={field.key} className="block">
                        <span className="mb-1.5 block text-sm font-semibold text-foreground">
                          {field.label}
                          {field.required && <RequiredMark />}
                        </span>
                        <select
                          value={form.category}
                          onChange={(inputEvent) => updateField("category", inputEvent.target.value)}
                          aria-invalid={Boolean(errorMessage)}
                          className={`ego-input ${
                            errorMessage
                              ? "border-[#dc2626] focus:border-[#dc2626] focus:ring-[rgba(220,38,38,0.12)]"
                              : ""
                          }`}
                        >
                          <option value="" disabled>
                            {activeCategories.length ? "Select category" : "No active categories available"}
                          </option>
                          {hasCurrentCategoryOption && (
                            <option value={form.category}>{form.category} (current)</option>
                          )}
                          {activeCategories.map((category) => (
                            <option key={category.id} value={category.name}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                        {errorMessage && <FieldError message={errorMessage} />}
                      </label>
                    );
                  }

                  if (field.key === "venue") {
                    return (
                      <label key={field.key} className="block">
                        <span className="mb-1.5 block text-sm font-semibold text-foreground">
                          {field.label}
                          {field.required && <RequiredMark />}
                        </span>
                        <select
                          value={form.venue}
                          onChange={(inputEvent) => updateVenueFromCatalog(inputEvent.target.value)}
                          aria-invalid={Boolean(errorMessage)}
                          className={`ego-input ${
                            errorMessage
                              ? "border-[#dc2626] focus:border-[#dc2626] focus:ring-[rgba(220,38,38,0.12)]"
                              : ""
                          }`}
                        >
                          <option value="" disabled>
                            {activeVenues.length ? "Select venue" : "No active venues available"}
                          </option>
                          {hasCurrentVenueOption && (
                            <option value={form.venue}>{form.venue} (current)</option>
                          )}
                          {activeVenues.map((venue) => (
                            <option key={venue.id} value={venue.name}>
                              {venue.name} - {venue.city}
                            </option>
                          ))}
                        </select>
                        {errorMessage && <FieldError message={errorMessage} />}
                      </label>
                    );
                  }

                  return (
                    <label key={field.key} className="block">
                      <span className="mb-1.5 block text-sm font-semibold text-foreground">
                        {field.label}
                        {field.required && <RequiredMark />}
                      </span>
                      <input
                        type={field.type ?? "text"}
                        value={form[field.key] as string}
                        onChange={(inputEvent) => updateField(field.key, inputEvent.target.value)}
                        disabled={
                          isOrganizer &&
                          (field.key === "organizerName" || field.key === "organizerEmail")
                        }
                        aria-invalid={Boolean(errorMessage)}
                        className={`ego-input ${
                          errorMessage
                            ? "border-[#dc2626] focus:border-[#dc2626] focus:ring-[rgba(220,38,38,0.12)]"
                            : ""
                        }`}
                      />
                      {errorMessage && <FieldError message={errorMessage} />}
                    </label>
                  );
                })}
              </div>

              <label className="mt-4 block">
                <span className="mb-1.5 block text-sm font-semibold text-foreground">
                  Address <RequiredMark />
                </span>
                <input
                  value={form.address}
                  onChange={(inputEvent) => updateField("address", inputEvent.target.value)}
                  aria-invalid={Boolean(errors.address)}
                  className={`ego-input ${
                    errors.address
                      ? "border-[#dc2626] focus:border-[#dc2626] focus:ring-[rgba(220,38,38,0.12)]"
                      : ""
                  }`}
                />
                {errors.address && <FieldError message={errors.address} />}
              </label>

              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-sm font-semibold text-foreground">Status</span>
                  <select
                    value={form.status}
                    onChange={(inputEvent) =>
                      setForm((current) => ({
                        ...current,
                        status: inputEvent.target.value as EventItem["status"],
                      }))
                    }
                    className="ego-input"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="sold-out">Sold out</option>
                    <option value="completed">Completed</option>
                  </select>
                </label>

                <label className="block">
                  <span className="mb-1.5 block text-sm font-semibold text-foreground">Public action</span>
                  <select
                    value={form.admissionType}
                    onChange={(inputEvent) =>
                      setForm((current) => ({
                        ...current,
                        admissionType: inputEvent.target.value as EventAdmissionType,
                      }))
                    }
                    className="ego-input"
                  >
                    <option value="ticket">Buy Tickets</option>
                    <option value="registration">Register now</option>
                  </select>
                </label>

                <label className="mt-7 flex items-center gap-3 text-sm font-semibold text-foreground">
                  <input
                    type="checkbox"
                    checked={form.featured}
                    onChange={(inputEvent) =>
                      setForm((current) => ({ ...current, featured: inputEvent.target.checked }))
                    }
                    className="h-4 w-4 rounded border-line text-[var(--pp-gold)] focus:ring-[var(--pp-gold)]"
                  />
                  Show as featured screening
                </label>
              </div>
            </section>

            <section>
              <h3 className="ego-eyebrow">Story</h3>
              <div className="mt-3 grid gap-4">
                <label className="block">
                  <span className="mb-1.5 block text-sm font-semibold text-foreground">
                    Description <RequiredMark />
                  </span>
                  <textarea
                    value={form.description}
                    onChange={(inputEvent) => updateField("description", inputEvent.target.value)}
                    rows={5}
                    aria-invalid={Boolean(errors.description)}
                    className={`ego-input resize-none ${
                      errors.description
                        ? "border-[#dc2626] focus:border-[#dc2626] focus:ring-[rgba(220,38,38,0.12)]"
                        : ""
                    }`}
                  />
                  {errors.description && <FieldError message={errors.description} />}
                </label>

                <label className="block">
                  <span className="mb-1.5 block text-sm font-semibold text-foreground">
                    Highlights <span className="font-normal text-[#9aa3b2]">(one per line)</span>
                  </span>
                  <textarea
                    value={form.highlights}
                    onChange={(inputEvent) =>
                      setForm((current) => ({ ...current, highlights: inputEvent.target.value }))
                    }
                    rows={4}
                    className="ego-input resize-none"
                  />
                </label>

                <div>
                  <span className="mb-1.5 block text-sm font-semibold text-foreground">Gallery</span>
                  <GalleryUploadField
                    value={form.gallery}
                    onChange={(next) => setForm((current) => ({ ...current, gallery: next }))}
                  />
                </div>
              </div>
            </section>
          </div>

          <div className="flex flex-col border-t border-line bg-surface-muted lg:min-h-0 lg:border-l lg:border-t-0">
            <div className="flex items-center justify-between border-b border-line px-5 py-4">
              <div>
                <h3 className="ego-eyebrow">Inventory</h3>
                <h4 className="mt-1 text-base font-bold text-foreground">Ticket tiers</h4>
              </div>
              <button
                type="button"
                onClick={() =>
                  setForm((current) => ({
                    ...current,
                    ticketTiers: [
                      ...current.ticketTiers,
                      {
                        name: "VIP",
                        description: "",
                        price: "150",
                        quantity: "100",
                        sold: "0",
                        perks: "",
                      },
                    ],
                  }))
                }
                className="ego-btn-secondary !py-1.5 !px-3 text-xs"
              >
                <Plus size={14} /> Add
              </button>
            </div>

            <div className="space-y-3 p-5 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
              {errors.ticketTiers && (
                <div className="rounded-lg border border-[rgba(220,38,38,0.18)] bg-[#fef2f2] dark:bg-[rgba(239,68,68,0.14)] px-3 py-2 text-xs font-semibold text-[#b91c1c]">
                  {errors.ticketTiers}
                </div>
              )}
              {form.ticketTiers.map((tier, index) => (
                <div
                  key={`${tier.id ?? "new"}-${index}`}
                  className="rounded-xl border border-line bg-surface p-4"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                      Tier {index + 1}
                    </div>
                    {form.ticketTiers.length > 1 && (
                      <button
                        type="button"
                        onClick={() =>
                          setForm((current) => ({
                            ...current,
                            ticketTiers: current.ticketTiers.filter((_, tierIndex) => tierIndex !== index),
                          }))
                        }
                        className="flex h-7 w-7 items-center justify-center rounded-md text-[#9aa3b2] transition hover:bg-[#fef2f2] dark:hover:bg-[rgba(239,68,68,0.14)] hover:text-[#dc2626]"
                        aria-label="Remove tier"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>

                  <div className="space-y-3">
                    {(
                      [
                        { label: "Name", key: "name", required: true },
                        { label: "Description", key: "description" },
                        { label: "Price", key: "price", type: "number", required: true },
                        { label: "Quantity", key: "quantity", type: "number", required: true },
                        { label: "Already sold", key: "sold", type: "number" },
                      ] satisfies ReadonlyArray<{
                        label: string;
                        key: keyof TierDraft;
                        type?: string;
                        required?: boolean;
                      }>
                    ).map((field) => {
                      const errorKey = `ticketTiers.${index}.${field.key}`;
                      const errorMessage = errors[errorKey];
                      return (
                        <label key={field.key} className="block">
                          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            {field.label}
                            {field.required && <RequiredMark />}
                          </span>
                          <input
                            type={field.type ?? "text"}
                            value={tier[field.key] as string}
                            onChange={(inputEvent) => updateTier(index, field.key, inputEvent.target.value)}
                            aria-invalid={Boolean(errorMessage)}
                            className={`ego-input !py-2 ${
                              errorMessage
                                ? "border-[#dc2626] focus:border-[#dc2626] focus:ring-[rgba(220,38,38,0.12)]"
                                : ""
                            }`}
                          />
                          {errorMessage && <FieldError message={errorMessage} />}
                        </label>
                      );
                    })}

                    <label className="block">
                      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Perks
                      </span>
                      <textarea
                        value={tier.perks}
                        onChange={(inputEvent) =>
                          setForm((current) => ({
                            ...current,
                            ticketTiers: current.ticketTiers.map((entry, tierIndex) =>
                              tierIndex === index ? { ...entry, perks: inputEvent.target.value } : entry
                            ),
                          }))
                        }
                        rows={3}
                        className="ego-input resize-none !py-2"
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>

            <div className="sticky bottom-0 z-10 flex gap-2 border-t border-line bg-surface-muted p-4 lg:static">
              <button type="button" onClick={onClose} className="ego-btn-secondary flex-1">
                Cancel
              </button>
              <button
                type="submit"
                form="event-editor-form"
                disabled={isSaving}
                className="ego-btn-primary flex-1"
              >
                {isSaving ? "Saving..." : event ? "Save" : "Create"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function GalleryUploadField({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const { token } = useAdminAuth();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const pickFiles = async (files: File[]) => {
    if (!token) {
      setError("Your session has expired. Please sign in again.");
      return;
    }

    const valid: File[] = [];
    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        setError("Only image files are allowed");
        continue;
      }
      if (file.size > 8 * 1024 * 1024) {
        setError("Each image must be smaller than 8 MB");
        continue;
      }
      valid.push(file);
    }
    if (valid.length === 0) return;

    setError(null);
    setUploadingCount((current) => current + valid.length);

    const uploaded: string[] = [];
    await Promise.all(
      valid.map(async (file) => {
        try {
          const { url } = await adminApi.uploadImage(token, file);
          uploaded.push(url);
        } catch (uploadError) {
          setError(uploadError instanceof Error ? uploadError.message : "Upload failed");
        } finally {
          setUploadingCount((current) => Math.max(0, current - 1));
        }
      })
    );

    if (uploaded.length > 0) {
      onChange([...value, ...uploaded]);
    }
  };

  return (
    <div className="space-y-3">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(inputEvent) => {
          const files = Array.from(inputEvent.target.files ?? []);
          if (files.length) void pickFiles(files);
          inputEvent.target.value = "";
        }}
      />

      {value.length === 0 && uploadingCount === 0 ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-line bg-surface-muted px-6 py-8 text-center transition hover:border-[var(--pp-gold)] hover:bg-[#fff7f3]"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)] text-[#c2410c]">
            <ImagePlus size={18} />
          </div>
          <span className="text-sm font-semibold text-foreground">Upload gallery images</span>
          <span className="text-xs text-muted-foreground">Select one or more · PNG, JPG, WebP · 8 MB each</span>
        </button>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {value.map((url, index) => (
            <div
              key={`${url}-${index}`}
              className="group relative overflow-hidden rounded-xl border border-line bg-[#0b1220]"
            >
              <img src={url} alt={`Gallery ${index + 1}`} className="h-28 w-full object-cover" />
              <button
                type="button"
                onClick={() => onChange(value.filter((_, itemIndex) => itemIndex !== index))}
                className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white opacity-0 backdrop-blur transition hover:bg-red-500 group-hover:opacity-100"
                aria-label="Remove image"
              >
                <X size={14} />
              </button>
            </div>
          ))}
          {Array.from({ length: uploadingCount }).map((_, index) => (
            <div
              key={`uploading-${index}`}
              className="flex h-28 items-center justify-center rounded-xl border border-dashed border-line bg-surface-muted"
            >
              <Loader2 size={18} className="animate-spin text-[var(--pp-gold)]" />
            </div>
          ))}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex h-28 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-line bg-surface-muted text-muted-foreground transition hover:border-[var(--pp-gold)] hover:bg-[#fff7f3] dark:hover:bg-[rgba(245,158,11,0.10)] hover:text-[var(--pp-gold)]"
          >
            <Plus size={18} />
            <span className="text-xs font-semibold">Add more</span>
          </button>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-[rgba(220,38,38,0.18)] bg-[#fef2f2] dark:bg-[rgba(239,68,68,0.14)] px-3 py-2 text-xs font-semibold text-[#b91c1c]">
          {error}
        </div>
      )}
    </div>
  );
}

function VideoUploadField({
  value,
  onChange,
  error: externalError,
}: {
  value: string;
  onChange: (url: string) => void;
  error?: string;
}) {
  const { token } = useAdminAuth();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pickFile = (file: File) => {
    if (!token) {
      setError("Your session has expired. Please sign in again.");
      return;
    }
    if (!file.type.startsWith("video/")) {
      setError("Please select a video file");
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      setError("Video must be smaller than 100 MB");
      return;
    }

    setError(null);
    setUploading(true);
    adminApi
      .uploadVideo(token, file)
      .then(({ url }) => {
        onChange(url);
      })
      .catch((uploadError: unknown) => {
        setError(uploadError instanceof Error ? uploadError.message : "Upload failed");
      })
      .finally(() => {
        setUploading(false);
      });
  };

  return (
    <div className="mt-3 space-y-3">
      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,video/mpeg,video/quicktime,video/webm,video/x-m4v"
        className="hidden"
        onChange={(inputEvent) => {
          const file = inputEvent.target.files?.[0];
          if (file) pickFile(file);
          inputEvent.target.value = "";
        }}
      />

      {value ? (
        <div className="relative overflow-hidden rounded-xl border border-line bg-[#0b1220]">
          <video src={value} controls preload="metadata" className="h-56 w-full bg-black object-contain" />
          <div className="flex items-center justify-between gap-2 bg-[#0b1220] p-3">
            <span className="truncate text-xs text-white/80">{value}</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="rounded-lg bg-white/15 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur transition hover:bg-white/25 disabled:opacity-60"
              >
                {uploading ? "Uploading..." : "Replace"}
              </button>
              <button
                type="button"
                onClick={() => onChange("")}
                className="rounded-lg bg-white/15 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur transition hover:bg-red-500/80"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className={`flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed bg-surface-muted px-6 py-10 text-center transition hover:border-[var(--pp-gold)] hover:bg-[#fff7f3] dark:hover:bg-[rgba(245,158,11,0.10)] disabled:cursor-not-allowed disabled:opacity-60 ${
            externalError ? "border-[#dc2626]" : "border-line"
          }`}
        >
          {uploading ? (
            <>
              <Loader2 size={22} className="animate-spin text-[var(--pp-gold)]" />
              <span className="text-sm font-semibold text-foreground">Uploading trailer...</span>
            </>
          ) : (
            <>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)] text-[#c2410c]">
                <Video size={20} />
              </div>
              <span className="text-sm font-semibold text-foreground">Click to upload optional trailer video</span>
              <span className="text-xs text-muted-foreground">MP4, MOV, MPEG, M4V, or WebM · up to 100 MB</span>
            </>
          )}
        </button>
      )}

      {(error || externalError) && (
        <div className="rounded-lg border border-[rgba(220,38,38,0.18)] bg-[#fef2f2] dark:bg-[rgba(239,68,68,0.14)] px-3 py-2 text-xs font-semibold text-[#b91c1c]">
          {error ?? externalError}
        </div>
      )}
    </div>
  );
}

function ImageUploadField({
  value,
  onChange,
  error: externalError,
  previewAlt = "Screening cover",
  requireLandscape = false,
}: {
  value: string;
  onChange: (url: string) => void;
  error?: string;
  previewAlt?: string;
  requireLandscape?: boolean;
}) {
  const { token } = useAdminAuth();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const readImageDimensions = (file: File) =>
    new Promise<{ width: number; height: number }>((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const image = new Image();

      image.onload = () => {
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
        URL.revokeObjectURL(objectUrl);
      };
      image.onerror = () => {
        reject(new Error("Could not read image dimensions"));
        URL.revokeObjectURL(objectUrl);
      };
      image.src = objectUrl;
    });

  const pickFile = async (file: File) => {
    if (!token) {
      setError("Your session has expired. Please sign in again.");
      return;
    }
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError("Image must be smaller than 8 MB");
      return;
    }

    setError(null);
    if (requireLandscape) {
      try {
        const dimensions = await readImageDimensions(file);
        if (dimensions.width <= dimensions.height) {
          setError("Upload a landscape image where width is greater than height.");
          return;
        }
      } catch (dimensionError) {
        setError(dimensionError instanceof Error ? dimensionError.message : "Could not read image dimensions");
        return;
      }
    }

    setUploading(true);
    adminApi
      .uploadImage(token, file)
      .then(({ url }) => {
        onChange(url);
      })
      .catch((uploadError: unknown) => {
        setError(uploadError instanceof Error ? uploadError.message : "Upload failed");
      })
      .finally(() => {
        setUploading(false);
      });
  };

  return (
    <div className="mt-3 space-y-3">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(inputEvent) => {
          const file = inputEvent.target.files?.[0];
          if (file) pickFile(file);
          inputEvent.target.value = "";
        }}
      />

      {value ? (
        <div className="relative overflow-hidden rounded-xl border border-line bg-[#0b1220]">
          <img src={value} alt={previewAlt} className="h-56 w-full object-cover" />
          <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-black/70 to-transparent p-3">
            <span className="truncate text-xs text-white/80">{value}</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="rounded-lg bg-white/15 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur transition hover:bg-white/25 disabled:opacity-60"
              >
                {uploading ? "Uploading..." : "Replace"}
              </button>
              <button
                type="button"
                onClick={() => onChange("")}
                className="rounded-lg bg-white/15 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur transition hover:bg-red-500/80"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className={`flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed bg-surface-muted px-6 py-10 text-center transition hover:border-[var(--pp-gold)] hover:bg-[#fff7f3] dark:hover:bg-[rgba(245,158,11,0.10)] disabled:cursor-not-allowed disabled:opacity-60 ${
            externalError ? "border-[#dc2626]" : "border-line"
          }`}
        >
          {uploading ? (
            <>
              <Loader2 size={22} className="animate-spin text-[var(--pp-gold)]" />
              <span className="text-sm font-semibold text-foreground">Uploading...</span>
            </>
          ) : (
            <>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)] text-[#c2410c]">
                <ImagePlus size={20} />
              </div>
              <span className="text-sm font-semibold text-foreground">Click to upload image</span>
              <span className="text-xs text-muted-foreground">
                PNG, JPG, or WebP · up to 8 MB{requireLandscape ? " · landscape only" : ""}
              </span>
            </>
          )}
        </button>
      )}

      {(error || externalError) && (
        <div className="rounded-lg border border-[rgba(220,38,38,0.18)] bg-[#fef2f2] dark:bg-[rgba(239,68,68,0.14)] px-3 py-2 text-xs font-semibold text-[#b91c1c]">
          {error ?? externalError}
        </div>
      )}
    </div>
  );
}

function RequiredMark() {
  return (
    <span className="ml-0.5 text-[#dc2626]" aria-hidden>
      *
    </span>
  );
}

function FieldError({ message }: { message: string }) {
  return (
    <p role="alert" className="mt-1 text-xs font-semibold text-[#dc2626]">
      {message}
    </p>
  );
}
